import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { ActionButton } from "@via/components/action-button/action-button";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import Drawer from "@via/components/drawer";
import { GenericError } from "@via/components/error";
import Table, { DrawerSize } from "@via/components/table/table";
import TableContextMenu from "@via/components/table/table-context-menu";
import { TruncatedText } from "@via/components/truncated-text";
import { useAbility } from "@via/context/ability-context";
import { useAuth } from "@via/hooks/useAuth";
import useUrlParams from "@via/hooks/useURLParams";
import EditWorkbenchForm from "@via/pages/workbenchs/edit-workbench-form";
import LaunchWorkbenchModal from "@via/pages/workbenchs/launch-workbench-modal";
import WorkbenchsFilter from "@via/pages/workbenchs/workbenchs-filter";
import {
  useDeleteWorkbench,
  useWorkbenchsList,
} from "@via/pages/workbenchs/workbenchs.api";
import {
  EMPTY_TABLE_MESSAGE,
  ERROR,
  MY_WORKBENCH_TABLE_OPTIONS,
  ROUTES,
  USER_ROLES,
  WorkbenchActions,
} from "@via/utils/constants";
import { getElapsedTime } from "@via/utils/helpers";
import { AppViewType, GetWorkbenchApplicationResponseBody } from "common";
import { useCallback, useEffect, useState } from "react";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext.tsx";

const defaultParams = {
  sort: [{ id: "dateCreated", desc: true }],
  filters: [{ id: "type", value: AppViewType.SharedWithMe }],
  page: 0,
  pageSize: 10,
};

const columnHelper = createColumnHelper<GetWorkbenchApplicationResponseBody>();

const MyWorkbenchs = (): JSX.Element => {
  const { user } = useAuth();
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();

  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >(initialParams.filters);
  const [openLaunchModal, setOpenLaunchModal] = useState(false);
  const [openEditDrawer, setEditDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<GetWorkbenchApplicationResponseBody>();
  const [tableData, setTableData] = useState<
    GetWorkbenchApplicationResponseBody[]
  >([]);
  const [workbenchToBeDeleted, setWorkbenchToBeDeleted] =
    useState<GetWorkbenchApplicationResponseBody | null>(null);

  const { mutateAsync: deleteWorkbench, isPending: isDeleteWorkbenchLoading } =
    useDeleteWorkbench();

  const {
    data: workbenchsList,
    isError: isWorkbenchsListError,
    isLoading: isWorkbenchsListLoading,
  } = useWorkbenchsList(page, pageSize, sort, columnFilters);

  const ability = useAbility([
    {
      action: "View",
      subject: "Button",
      condition: false,
    },
  ]);

  useEffect(() => {
    if (workbenchsList?.data) {
      setTableData(workbenchsList.data);
    }
  }, [workbenchsList]);

  const handleContextMenuAction = (
    name: string,
    workbench: GetWorkbenchApplicationResponseBody
  ): void => {
    switch (name) {
      case WorkbenchActions.Launch:
        setSelectedRow(workbench);
        setOpenLaunchModal(true);
        break;
      case WorkbenchActions.Edit:
        setSelectedRow(workbench);
        setEditDrawerOpen(true);
        break;
      case WorkbenchActions.Remove:
        setWorkbenchToBeDeleted(workbench);
        break;
    }
  };

  useEffect(() => {
    if (!openEditDrawer) {
      setSelectedRow(undefined);
    }
  }, [openEditDrawer]);

  useEffect(() => {
    if (!openLaunchModal) {
      setSelectedRow(undefined);
    }
  }, [openLaunchModal]);

  useEffect(() => {
    setUrlParams({ sort, filters: columnFilters, page, pageSize });
  }, [sort, columnFilters, page, pageSize]);

  const getMyWorkbenchsContext = useCallback(() => {
    const workbenchs = workbenchsList?.data || [];

    return {
      workbenchs: workbenchs.map(workbench => ({
        id: workbench.id,
        name: workbench.name,
        description: workbench.description,
        dateCreated: workbench.dateCreated,
        dateModified: workbench.dateModified,
        owner: workbench.owner?.username,
        own: workbench.own,
      })),
      total: workbenchsList?.total || 0,
      currentPage: page,
      pageSize: pageSize,
      sortBy: sort.length > 0 ? sort[0] : null,
      filters: columnFilters,
    };
  }, [
    workbenchsList?.data,
    workbenchsList?.total,
    page,
    pageSize,
    sort,
    columnFilters,
  ]);

  useRegisterCortexContext({
    routePattern: ROUTES.AVAILABLE_WOKBENCH,
    contextName: "My Workbenchs",
    getContext: getMyWorkbenchsContext,
    systemPrompt:
      "You are analyzing my workbenchs list page. Help answer questions about workbenchs, help find specific workbenchs, and assist with filtering and sorting.",
    enabled: !!workbenchsList,
  });

  const columns = [
    columnHelper.accessor("name", {
      cell: ({ row }) => {
        return (
          <div className="flex flex-row items-center">
            {row.original.owner?.username === user?.username ||
            ability.can("View", "Button") ? (
              <button
                className="via-link truncate"
                onClick={e => {
                  e.preventDefault();
                  handleContextMenuAction("Edit", row.original);
                }}>
                <TruncatedText
                  text={row.original.name}
                  maxLength={50}
                  id={`tooltip-${row.original.id}-${row.original.name}`}
                />
              </button>
            ) : (
              <span className="truncate">
                <TruncatedText
                  text={row.original.name}
                  maxLength={50}
                  id={`tooltip-${row.original.id}-${row.original.name}`}
                />
              </span>
            )}
          </div>
        );
      },
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor("description", {
      cell: info => info.getValue(),
      header: () => <span>Description</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("dateModified", {
      cell: info => {
        const value = info.getValue();
        return <span>{getElapsedTime(value)}</span>;
      },
      header: () => <span>Last Modified</span>,
    }),
    columnHelper.accessor("dateCreated", {
      cell: info => {
        const value = info.getValue();
        return <span>{getElapsedTime(value)}</span>;
      },
      header: () => <span>Date Created</span>,
    }),
    columnHelper.accessor("owner", {
      cell: ({ row }) => {
        return <span>{row.original.owner?.username}</span>;
      },
      header: () => "Owner",
      enableSorting: false,
    }),
    columnHelper.accessor("id", {
      cell: ({ row }) => {
        const isOwner = row.original?.own;
        const isAdmin = user?.userRole === USER_ROLES.ADMIN;
        const contextMenuOptions = MY_WORKBENCH_TABLE_OPTIONS;

        return (
          <>
            {isAdmin || isOwner ? (
              <TableContextMenu
                options={contextMenuOptions}
                onSelect={option => {
                  handleContextMenuAction(option.name, row.original);
                }}
              />
            ) : (
              <ActionButton
                button={WorkbenchActions.Launch}
                primary={true}
                showIcon={false}
                onClick={() => {
                  handleContextMenuAction(
                    WorkbenchActions.Launch,
                    row.original
                  );
                }}
              />
            )}
          </>
        );
      },
      header: () => "Options",
      size: 50,
      enableSorting: false,
    }),
  ] as Array<ColumnDef<GetWorkbenchApplicationResponseBody>>;

  return (
    <main className="py-4">
      {isWorkbenchsListError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <Table
          isLoading={isWorkbenchsListLoading}
          data={tableData}
          columns={columns}
          sort={sort}
          setSort={setSort}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={workbenchsList?.total}
          emptyMessage={EMPTY_TABLE_MESSAGE.WORKBENCH}
          applyColumnSize={true}
          additionalFilters={
            ability.can("View", "Button") ? (
              <WorkbenchsFilter
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                setPage={setPage}
                setPageSize={setPageSize}
              />
            ) : null
          }
        />
      )}
      <DeleteModal
        item={workbenchToBeDeleted}
        setItem={setWorkbenchToBeDeleted}
        deleteFunction={() =>
          deleteWorkbench(workbenchToBeDeleted!.id.toString())
        }
        isLoading={isDeleteWorkbenchLoading}
        title="Delete Workbench"
      />
      <Drawer
        id="my-workbenchs-edit"
        open={openEditDrawer}
        setOpen={setEditDrawerOpen}
        size={DrawerSize.Medium}>
        <EditWorkbenchForm
          setOpen={setEditDrawerOpen}
          workbench={selectedRow}
        />
      </Drawer>
      {openLaunchModal && (
        <LaunchWorkbenchModal
          workbenchId={selectedRow!.id}
          open={openLaunchModal}
          setOpen={() => setOpenLaunchModal(false)}
        />
      )}
    </main>
  );
};

export default MyWorkbenchs;
